import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Lock, Mic } from "lucide-react";

import { cn } from "@/lib/utils";
import { getOpenclawConfig, type OpenclawConfig } from "@/api/lx-openclaw-config";
import { isDisabledByOrg } from "@/lib/openclaw-policy";
import { VoiceControls } from "@/components/settings/VoiceControls";

/**
 * `VoiceChip` — voice on the hero, the LAST slice (masterplan §3.6, S6).
 *
 * The chip label shows the EFFECTIVE state — `🎙 Camila` / `🎙 desativada` /
 * a lock when the org disabled voice — and its popover hosts the extracted
 * `VoiceControls`, so the toggle, the org-curated picker and the 409
 * `VOICE_NOT_ALLOWED` race all keep their exact shipped behavior.
 *
 * ⚠ **Visibility rule (live-walk W-5, and the reason this is the last slice):**
 * the chip renders ONLY when it is actionable — the org curated at least one
 * voice, OR TTS is already on. Otherwise it is ABSENT. galvão's org has zero
 * `allowedVoices` today, so the honest outcome there is no chip at all: a hero
 * must never advertise a dead knob, and voice stays reachable through the gear
 * dialog's Voz entry regardless.
 */
export function VoiceChip() {
  const { t } = useTranslation();
  const [config, setConfig] = useState<OpenclawConfig | null>(null);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    getOpenclawConfig()
      .then((c) => !cancelled && setConfig(c))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Click-outside + Esc close the popover. Esc is capture-phase +
  // stopImmediatePropagation so it never also reaches FocusPaneHost and closes
  // the pane underneath — the same discipline as every hero dialog.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  if (!config) return null;

  const lockedByOrg = isDisabledByOrg(config.org, "voice");
  const allowedVoices = config.org.voice?.allowedVoices ?? [];
  const enabled = config.user?.voice?.tts?.enabled ?? false;
  const selected = config.user?.voice?.tts?.selectedVoice ?? "";

  // The §3.6 rule: actionable, or absent.
  const actionable = allowedVoices.length > 0 || enabled;
  if (!actionable) return null;

  const label = lockedByOrg
    ? t("agent.hero.voice.locked")
    : enabled
      ? selected || t("agent.hero.voice.on")
      : t("agent.hero.voice.off");

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        data-testid="voice-chip"
        data-state={lockedByOrg ? "locked" : enabled ? "on" : "off"}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={t("agent.hero.voice.aria", { state: label })}
        title={t("agent.hero.voice.aria", { state: label })}
        className={cn(
          "flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors",
          enabled && !lockedByOrg
            ? "border-gold-500/60 bg-gold-400/15 text-gold-700 hover:bg-gold-400/25 dark:text-gold-400"
            : "border-cream-300 text-coffee-500 hover:bg-cream-100 dark:border-coffee-700 dark:text-cream-500 dark:hover:bg-coffee-800",
        )}
      >
        {lockedByOrg ? <Lock size={10} aria-hidden /> : <Mic size={10} aria-hidden />}
        <span className="max-w-[9rem] truncate">{label}</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t("agent.hero.voice.popoverTitle")}
          data-testid="voice-chip-popover"
          className="absolute right-0 top-full z-40 mt-1.5 w-80 rounded-xl border border-cream-200 bg-white p-3 text-left shadow-xl dark:border-coffee-700 dark:bg-coffee-900"
        >
          <h3 className="mb-2 font-serif-display text-base text-coffee-900 dark:text-cream-100">
            {t("agent.hero.voice.popoverTitle")}
          </h3>
          {/* `chrome={false}` drops the tab's Card + heading — the popover is
              the chrome here. Everything else (write path, locks, 409) is the
              shipped logic untouched. */}
          <VoiceControls chrome={false} />
        </div>
      )}
    </div>
  );
}
